import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KAART_STATUSSEN,
  afkoppelStatus,
  type KaartStatus,
  type Registratie,
} from '@/types/domain';
import { isHuurwoning } from '@/data/huurwoningen';
import { getConfig } from '@/config/appConfig';
import { berekenStats } from '@/utils/stats';
import { adresRegel } from '@/utils/format';
import { useApp } from '@/state/AppContext';
import { Knop } from '@/components/ui/Knop';
import { Laadvlak } from '@/components/ui/Feedback';
import { Keuzeveld, Tekstveld } from '@/components/ui/Velden';
import { VernieuwIcoon } from '@/components/layout/Iconen';
import { StatTegels } from '@/components/dashboard/StatTegels';
import { RegistratieTabel } from '@/components/dashboard/RegistratieTabel';
import { Bureaulogo } from '@/components/layout/Merk';
import { ExportPaneel } from '@/components/dashboard/ExportPaneel';
import '@/components/dashboard/dashboard.css';

export function DashboardPagina(): JSX.Element {
  const {
    registraties,
    woningen,
    ladenRegistraties,
    herlaadRegistraties,
    openRegistratie,
    toonMelding,
  } = useApp();
  const navigeer = useNavigate();

  const [zoekterm, setZoekterm] = useState('');
  const [statusFilter, setStatusFilter] = useState<KaartStatus | ''>('');
  const [coachFilter, setCoachFilter] = useState<string>('');

  const coaches = useMemo(
    () =>
      [...new Set(registraties.map((r) => r.coach).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'nl')
      ),
    [registraties]
  );

  const gefilterd = useMemo(() => {
    const term = zoekterm.trim().toLowerCase();
    return registraties.filter((registratie) => {
      if (
        statusFilter &&
        afkoppelStatus(registratie, isHuurwoning(registratie.adres)) !== statusFilter
      ) {
        return false;
      }
      if (coachFilter && registratie.coach !== coachFilter) return false;
      if (!term) return true;

      const zoekbaar = [
        adresRegel(registratie.adres),
        registratie.adres.postcode,
        registratie.adres.plaats,
        registratie.bagId,
        registratie.coach,
        registratie.uitkomstToelichting,
        registratie.algemeneToelichting,
        registratie.voortuin.toelichting,
        registratie.achtertuin.toelichting,
      ]
        .join(' ')
        .toLowerCase();

      return zoekbaar.includes(term);
    });
  }, [registraties, zoekterm, statusFilter, coachFilter]);

  /*
   * De cijfers worden over dezelfde woningen geteld als waarover de kaart
   * kleurt: alles in het plangebied, plus de handmatig geplaatste punten die
   * niet in de BAG staan. Zo kan het dashboard de kaart niet tegenspreken.
   */
  const stats = useMemo(() => berekenStats(woningen, registraties), [woningen, registraties]);
  const totaal = stats.totaalWoningen;

  /** Vanuit de tabel doorklikken naar de woning op de kaart. */
  const openOpKaart = (registratie: Registratie) => {
    openRegistratie(registratie);
    navigeer('/');
  };

  if (ladenRegistraties && registraties.length === 0) {
    return (
      <div className="dashboard">
        <Laadvlak tekst="Registraties laden..." />
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header className="dashboard__kop">
        <div className="dashboard__titel">
          <h1>Dashboard</h1>
          <div className="dashboard__ondertitel">
            {registraties.length} van {totaal} woningen · bron:{' '}
            {getConfig().dataProvider === 'supabase' ? 'Supabase' : 'lokale mockdata'}
          </div>
        </div>

        <div className="dashboard__acties">
          <Knop
            variant="secundair"
            icoon={<VernieuwIcoon />}
            onClick={() => void herlaadRegistraties()}
            bezig={ladenRegistraties}
          >
            Vernieuwen
          </Knop>
        </div>
      </header>

      <StatTegels stats={stats} />

      <ExportPaneel registraties={gefilterd} bijMelding={toonMelding} />

      <div className="filters">
        <Tekstveld
          label="Zoeken"
          type="search"
          waarde={zoekterm}
          bijWijziging={setZoekterm}
          placeholder="Adres, coach of toelichting"
        />
        <Keuzeveld<KaartStatus>
          label="Afkoppelen van het pand"
          waarde={statusFilter}
          opties={KAART_STATUSSEN}
          bijWijziging={setStatusFilter}
          legeOptie="Alle statussen"
        />
        <Keuzeveld<string>
          label="Afkoppelcoach"
          waarde={coachFilter}
          opties={coaches}
          bijWijziging={setCoachFilter}
          legeOptie="Alle coaches"
        />
      </div>

      <RegistratieTabel registraties={gefilterd} bijSelectie={openOpKaart} />

      {/* Het dashboard is waar de gemeente meekijkt en waar de exports
          vandaan komen. Daar hoort te staan van wie dit werk is. */}
      <footer className="dashboard__voet">
        <Bureaulogo hoogte={30} />
        <span>Afkoppelcoach · project GM-26000752, gemeente Geertruidenberg</span>
      </footer>
    </div>
  );
}
